import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import SPAButton from '../shared/SPAButton';
import SPACheckbox from '../shared/SPACheckbox';
import SPADataTable from '../shared/SPADataTable';
import SPADialog from '../shared/SPADialog';
import RoutedSPADialog from '../shared/RoutedSPADialog';
import SPAPill from '../shared/SPAPill';
import type { SPAPillTone } from '../shared/SPAPill';
import useAssignmentSyncState from './useAssignmentSyncState';
import { GradeSyncLink } from './gradeSyncPath';
import { useSectionSearch } from './SectionTabs';

export type Verdict = 'ours' | 'repointable' | 'misclaimed' | 'theirs' | 'unresolved';

export interface LineItem {
  id: string;
  label: string;
  score_maximum: number | null;
  resource_id: string | null;
  resource_title: string | null;
  held_by_title: string | null;
  course_id: string;
  /** The assignment the LMS names belongs to another teacher — a Blueprint copy, claimable by title. */
  foreign: boolean;
  memberships_url: string;
  held_by: number | null;
  /** Other TCI assignments holding this column's resourceLinkId — a launch may open them instead. */
  link_held_by: number[];
  resource_link_id: string | null;
  title_match: boolean;
  verdict: Verdict;
}

interface Course {
  id: string;
  line_items: LineItem[];
  error?: string;
  title?: string | null;
}

interface Row extends LineItem {
  courseTitle: string;
  /** Assignees of this assignment whom the LMS places in this column's course — the students whose
   *  scores actually route here. */
  routed: number | null;
  /** Everyone the LMS has in that course, assigned here or not. */
  learners: number | null;
  /** The line item's own id. One assignment has one column per course, and without this they read
   *  as the same row repeated. */
  itemId: string;
  /** The LMS and TCI disagree about who owns this column — the fault behind most real cases. */
  mismatch: boolean;
}

// AIDEV-NOTE: "Linked here" first — those are this assignment's own columns, the context every other
// row is read against. Then the ones it could claim, then everything else.
const VERDICTS: Record<Verdict, { label: string; tone: SPAPillTone; rank: number }> = {
  misclaimed: { label: 'Belongs here, used by another', rank: 1, tone: 'error' },
  ours: { label: 'Connected', rank: 0, tone: 'success' },
  repointable: { label: 'Belongs here, not connected', rank: 2, tone: 'warning' },
  theirs: { label: 'Another assignment', rank: 4, tone: 'neutral' },
  unresolved: { label: 'Its assignment was deleted', rank: 3, tone: 'warning' },
};

// Both sides of a column name a TCI assignment, so both link to it and both show what it is.
const AssignmentRef = ({ id, mismatch, title }: {
  id: string | number | null;
  mismatch: boolean;
  title: string | null;
}) => {
  if (id == null) return <span className="tw-text-spa-tertiary">nobody</span>;

  return (
    <GradeSyncLink
      className={mismatch ? 'tw-text-spa-error hover:tw-text-spa-error' : undefined}
      to={`/assignments/${id}`}
    >
      <span className="tw-font-mono tw-text-xs">{id}</span>
      {title != null && <span className="tw-block tw-text-sm tw-text-spa-secondary">{title}</span>}
    </GradeSyncLink>
  );
};

// AIDEV-NOTE: Mirrors LmsGradeSyncRepointService's tiers so the button says what will happen before it
// happens. The server re-checks all of this — this is signposting, never the guard. A `theirs` column is
// offered only when its title matches and it names another teacher's work: the Blueprint copy. One naming
// this teacher's own assignment belongs to that assignment, and is fixed from there.
const action = (item: LineItem): { label: string; confirm: boolean } | null => {
  if (item.verdict === 'ours') return null;
  if (item.verdict === 'theirs') {
    return item.title_match && item.foreign ? { confirm: true, label: 'Connect (title matches)' } : null;
  }
  if (item.verdict === 'unresolved') return { confirm: true, label: 'Connect anyway' };

  return { confirm: false, label: 'Connect here' };
};

const column = createColumnHelper<Row>();

const buildColumns = (
  onAct: (row: Row, confirm: boolean) => void,
  busy: boolean,
): ColumnDef<Row, unknown>[] => [
  column.accessor('verdict', {
    cell: info => (
      <SPAPill tone={VERDICTS[info.getValue()].tone}>{VERDICTS[info.getValue()].label}</SPAPill>
    ),
    header: 'Status',
  }),
  // AIDEV-NOTE: The line item id earns its place: a Blueprint copies one assignment into every
  // associated course, so four rows can share a title, a resourceId and a holder while being four
  // genuinely different gradebook columns. Without the id they read as the same row repeated.
  column.accessor('label', {
    cell: info => (
      <div>
        <div className="tw-text-spa-primary">
          {info.getValue()}
          {info.row.original.title_match && info.row.original.verdict !== 'ours' && (
            <SPAPill className="tw-ml-2" tone="info">Title matches</SPAPill>
          )}
        </div>
        <div className="tw-text-sm tw-text-spa-tertiary">
          {info.row.original.courseTitle}
          <span className="tw-font-mono">{` · column ${info.row.original.itemId}`}</span>
        </div>
      </div>
    ),
    header: 'Gradebook column',
    meta: { width: 'auto' },
  }),
  // AIDEV-NOTE: These two columns exist to be read against each other. When they disagree, the LMS
  // points at one assignment while TCI posts from another — a silent overwrite at best, nothing
  // delivered at worst. The row says so rather than leaving it to be spotted.
  // AIDEV-NOTE: The number that explains the whole table. Lms::LtiWorker resolves a course per
  // student: it walks the sections, asks each course's roster whether that student is in it, and
  // posts to the first that says yes. So one assignment with four columns is not four copies of the
  // same thing — it is four destinations, and each student's score goes to exactly one of them.
  //
  // Deliberately two numbers, because three different populations get confused here: everyone the
  // LMS has in the course, everyone TCI assigned, and the intersection — which is the only group
  // whose scores can actually land. A TCI student who never launched has no LMS identity and counts
  // in none of them.
  column.accessor('routed', {
    cell: (info) => {
      const routed = info.getValue();
      const { learners } = info.row.original;

      if (routed == null) return <span className="tw-text-spa-tertiary">—</span>;

      return (
        <span className={routed === 0 ? 'tw-text-spa-tertiary' : undefined}>
          {routed}
          {learners != null && (
            <span className="tw-text-spa-tertiary">{` of ${learners}`}</span>
          )}
        </span>
      );
    },
    header: 'Students here',
    meta: { align: 'right' },
  }),
  column.accessor('resource_id', {
    cell: info => (
      <AssignmentRef
        id={info.getValue()}
        mismatch={info.row.original.mismatch}
        title={info.row.original.resource_title}
      />
    ),
    header: 'The LMS says it is for',
    meta: { width: '13rem' },
  }),
  column.display({
    cell: info => (info.row.original.mismatch
      ? (
        <span
          aria-label="disagrees"
          className="tw-font-semibold tw-text-spa-error"
          title="The LMS and TCI disagree about who owns this column"
        >
          ≠
        </span>
      )
      : <span aria-hidden="true" className="tw-text-spa-tertiary">=</span>),
    header: '',
    id: 'match',
    meta: { align: 'center', width: 1 },
  }),
  column.accessor('held_by', {
    cell: info => (
      <AssignmentRef
        id={info.getValue()}
        mismatch={info.row.original.mismatch}
        title={info.row.original.held_by_title}
      />
    ),
    header: 'Sending grades to it now',
    meta: { width: '13rem' },
  }),
  column.display({
    cell: (info) => {
      const act = action(info.row.original);

      if (act == null) return null;

      return (
        <SPAButton
          disabled={busy}
          size="xs"
          type="button"
          variant={act.confirm ? 'secondary' : 'primary'}
          onClick={() => onAct(info.row.original, act.confirm)}
        >
          {act.label}
        </SPAButton>
      );
    },
    header: '',
    id: 'action',
    meta: { width: 1 },
  }),
];

const WHY: Partial<Record<Verdict, (row: Row) => string>> = {
  theirs: row => `The LMS says this column belongs to assignment ${row.resource_id}, another teacher's — `
    + 'usually a Blueprint copy. Its title matches this assignment.',
  unresolved: row => `The LMS says this column belongs to assignment ${row.resource_id ?? 'nothing'}, which `
    + 'no longer exists — usually deleted by an annual reset.',
};

interface RepointReviewProps {
  busy: boolean;
  clearOthers: boolean;
  onCancel: () => void;
  onClearOthers: (value: boolean) => void;
  onConfirm: () => void;
  row: Row;
}

const RepointReview = ({
  busy, clearOthers, onCancel, onClearOthers, onConfirm, row,
}: RepointReviewProps) => {
  const others = row.link_held_by;
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), [row.id]);

  return (
    <div
      ref={ref}
      className="tw-mb-3 tw-rounded tw-border tw-border-solid tw-border-spa-border tw-bg-spa-bg-tertiary tw-px-3
        tw-py-2"
    >
      {WHY[row.verdict] != null && (
        <p className="tw-m-0 tw-mb-1 tw-text-sm tw-text-spa-secondary">{WHY[row.verdict]?.(row)}</p>
      )}
      <p className="tw-m-0 tw-text-sm tw-text-spa-primary">
        {`Connect this assignment to the "${row.label}" column in ${row.courseTitle}.`}
        {row.resource_link_id != null && ' Opening it from the LMS will open this assignment.'}
      </p>
      {others.length > 0 && (
        <SPACheckbox
          checked={clearOthers}
          className="tw-mt-2"
          id="clear-others"
          labelClassName="tw-text-spa-secondary"
          onChange={e => onClearOthers(e.target.checked)}
        >
          {`Also clear the launch link on ${others.length === 1 ? 'assignment' : 'assignments'} `}
          {`${others.join(', ')}. Left alone, a launch from this column may still open that one.`}
        </SPACheckbox>
      )}
      <div className="tw-mt-2 tw-flex tw-gap-2">
        <SPAButton type="button" variant="secondary" onClick={onCancel}>Cancel</SPAButton>
        <SPAButton disabled={busy} type="button" variant="primary" onClick={onConfirm}>
          Connect it
        </SPAButton>
      </div>
    </div>
  );
};

// An empty table after a failed read is not evidence the assignment was never added to the LMS.
const emptyMessage = (showAll: boolean, unreadable: number) => {
  if (unreadable > 0) return 'No columns to show — the courses above could not be read.';
  if (showAll) return "No column in any of this teacher's courses — it has never been added to the LMS.";

  return "None of the columns in this teacher's courses is for this assignment. Show every column to pick one "
    + 'by hand.';
};

// AIDEV-NOTE: Routed rather than state-driven so a support agent can paste the link to exactly the
// column list they are looking at — the SPA's default for modals.
// AIDEV-NOTE: At copies/:copyId/columns it picks for that copy over the page being viewed, so linking a
// sibling copy from the Copies panel never leaves the page; afterwards it returns there, not to the copy.
const LineItemPicker = () => {
  const { copyId, id: pageId = '' } = useParams();
  const assignmentId = copyId ?? pageId;
  const sectionSearch = useSectionSearch();
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(true);
  const [reviewing, setReviewing] = useState<{ row: Row; confirm: boolean } | null>(null);
  const [clearOthers, setClearOthers] = useState(false);
  const queryClient = useQueryClient();

  // Same query key as the page behind the dialog, so this reads its cache rather than refetching.
  const { data: page } = useAssignmentSyncState(assignmentId);

  const { data, error, isFetching } = useQuery({
    queryFn: async (): Promise<{ courses?: Course[]; error?: string }> => {
      const { data: body } = await axios.get(
        Routes.plato_api_sysadmin_grade_sync_line_items_path(assignmentId),
        { params: { all: showAll } },
      );

      return body.data;
    },
    queryKey: ['gradeSyncLineItems', assignmentId, showAll],
  });

  const repoint = useMutation({
    mutationFn: async ({ confirmed, item }: { confirmed: boolean; item: LineItem }) => axios.post(
      Routes.plato_api_sysadmin_grade_sync_repoint_path(assignmentId),
      { clear_others: clearOthers, confirmed, line_items_url: item.id, memberships_url: item.memberships_url },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradeSyncLineItems', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncVerify', assignmentId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncRepairs'] });
      setReviewing(null);
      if (copyId == null) {
        navigate({ pathname: '..', search: '?verify=1' });
      } else {
        queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignment', pageId] });
        navigate(`..${sectionSearch}`);
      }
    },
  });

  const rosterFor = (courseId: string) => (page?.roster?.courses ?? [])
    .find(c => c.course_id === courseId);

  const rows = useMemo<Row[]>(() => (data?.courses ?? [])
    .flatMap(course => course.line_items.map(item => ({
      ...item,
      courseTitle: course.title || `Course ${course.id}`,
      // Canvas line_items/<id>, Schoology lineitems/<id>.
      itemId: (item.id.match(/line_?items\/(\d+)/) || [])[1] ?? item.id,
      learners: rosterFor(course.id)?.learners ?? null,
      mismatch: item.held_by != null && item.resource_id !== String(item.held_by),
      // From the roster, not the student table: that list is capped at 200 rows, and this figure
      // has to be right for a big assignment too.
      routed: rosterFor(course.id)?.matched_in_tci ?? null,
    })))
    // AIDEV-NOTE: Disagreements first, not verdicts first. A column both sides agree belongs to a
    // third assignment is verdict `theirs` and ranks last, yet it is exactly what someone opened this
    // dialog to find — sorting by verdict alone buried every mismatch below the healthy rows.
    .sort((a, b) => (
      VERDICTS[a.verdict].rank - VERDICTS[b.verdict].rank
      || Number(b.mismatch) - Number(a.mismatch)
      || Number(b.title_match) - Number(a.title_match)
    )), [data, page]);

  // Every action is reviewed first: it now moves the launch link as well as the gradebook pointer.
  const onAct = (row: Row, confirm: boolean) => {
    setClearOthers(false);
    setReviewing({ confirm, row });
  };

  const refusal = (repoint.error as { response?: { data?: { errors?: string[] } } })
    ?.response?.data?.errors?.[0];
  const mismatches = rows.filter(r => r.mismatch).length;
  const unreadable = (data?.courses ?? []).filter(c => c.error != null);

  return (
    <RoutedSPADialog returnPath={`..${sectionSearch}`}>
      <SPADialog.Content size="xl">
        <SPADialog.Header>
          <SPADialog.Title>
            {copyId == null ? 'LMS gradebook columns' : `LMS gradebook columns for copy ${copyId}`}
          </SPADialog.Title>
          <SPADialog.Description>
            An assignment has one gradebook column in each LMS course it was added to, and each
            student&apos;s grade goes to the course they are in — so several &ldquo;Connected&rdquo; rows
            are normal. Connecting a column changes only TCI&apos;s side; nothing in the LMS is created,
            renamed or deleted.
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
            <SPACheckbox
              checked={showAll}
              id="picker-all"
              labelClassName="tw-text-spa-secondary"
              onChange={e => setShowAll(e.target.checked)}
            >
              Show every column, not just this assignment&apos;s
            </SPACheckbox>
            {mismatches > 0 && (
              <SPAPill tone="error">
                {`${mismatches} ${mismatches === 1 ? 'column disagrees' : 'columns disagree'}`}
              </SPAPill>
            )}
          </div>

          {(error != null || data?.error != null) && (
            <p className="tw-m-0 tw-mb-3 tw-text-sm tw-text-spa-error">
              {data?.error ?? 'Could not reach the LMS.'}
            </p>
          )}
          {refusal != null && <p className="tw-m-0 tw-mb-3 tw-text-sm tw-text-spa-error">{refusal}</p>}
          {unreadable.length > 0 && (
            <div className="tw-mb-3 tw-text-sm tw-text-spa-error">
              {`Could not read the gradebook in ${unreadable.length} course${unreadable.length === 1 ? '' : 's'}:`}
              <ul className="tw-m-0 tw-mt-1 tw-pl-5">
                {unreadable.map(course => (
                  <li key={course.id}>{`Course ${course.id} — ${course.error}`}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Above the table: the list runs to dozens of rows, and below it the review was off-screen. */}
          {reviewing != null && (
            <RepointReview
              busy={repoint.isLoading}
              clearOthers={clearOthers}
              row={reviewing.row}
              onCancel={() => setReviewing(null)}
              onClearOthers={setClearOthers}
              onConfirm={() => repoint.mutate({ confirmed: reviewing.confirm, item: reviewing.row })}
            />
          )}

          <SPADataTable
            columns={buildColumns(onAct, repoint.isLoading)}
            data={rows}
            defaultSort={[]}
            emptyMessage={emptyMessage(showAll, unreadable.length)}
            footnote={(
              <>
                &ldquo;Assignees here&rdquo; counts this assignment&apos;s students that the LMS
                places in that course, against everyone the course contains — a student who has never
                launched has no LMS identity and appears in neither. &ldquo;LMS says&rdquo; is the
                assignment the column was created for; &ldquo;TCI has&rdquo; is the assignment posting
                to it. When they differ, one assignment&apos;s grades land in another&apos;s column.
              </>
            )}
            isLoading={isFetching}
            loadingRows={4}
            rowClassName={row => (row.mismatch ? 'tw-bg-spa-bg-tertiary' : '')}
          />

        </SPADialog.Body>

        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton type="button" variant="secondary">Close</SPAButton>
          </SPADialog.Close>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default LineItemPicker;
